// The calendar arithmetic behind the issuing analytics page's window.
//
// Three rules govern this file, and each of them is load-bearing:
//
//   1. Every date here is a `yyyy-mm-dd` **clinic-local calendar date**, never an
//      instant. That is what the API takes (see `IssuingAnalyticsQuery`), so a
//      `toISOString()` anywhere in this file would be a bug rather than a
//      formatting choice. Nothing below constructs a `Date` for arithmetic —
//      only for formatting a label, where the timezone is pinned to UTC so the
//      string that comes out names the same day that went in.
//
//   2. This file does NOT decide where a bucket starts. The trend chart's axis
//      is `members.time_bucket` from the payload, because the server truncates
//      with Postgres's rules (a week bucket starts on the Monday, which can be
//      earlier than the requested `from`) and a second implementation of those
//      rules here would only be right until it wasn't. What the presets below
//      call "this week" and "this month" is a different question — it is what
//      the *user* means by picking a chip — and is answered here.
//
//   3. Every function is total. A malformed date, a reversed range and a range
//      of a hundred years all have defined answers, because the window can
//      arrive from a hand-edited URL and a chart is the wrong place to discover
//      that.

import { addDays, diffDays, isValidIsoDate } from '@/lib/calendar';
// `MAX_RANGE_DAYS` is the server's cap, not a second opinion about it. A client
// that trims to a different number than the server rejects at produces the one
// failure the user cannot act on — a picker offering a range the request 400s on.
import { BUCKET_GRANULARITIES, MAX_RANGE_DAYS } from '@/lib/analytics';
import type { BucketGranularity } from '@/lib/analytics';
import { localeFor } from '@/lib/locale';

export const PERIOD_PRESETS = ['today', 'week', 'month', 'last30', 'last90', 'custom'] as const;

export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

/** The presets that resolve to a range on their own; `custom` needs the user's dates. */
export type FixedPeriodPreset = Exclude<PeriodPreset, 'custom'>;

/** Both bounds inclusive, as the API reads them. */
export interface CalendarRange {
  readonly from: string;
  readonly to: string;
}

export interface ResolvedWindow extends CalendarRange {
  readonly bucket: BucketGranularity;
  /** Whole days covered, inclusive of both bounds — never less than 1. */
  readonly days: number;
  /**
   * Whether the window reaches the present day. Carried rather than re-derived
   * because "nothing was issued in these 90 days" and "this clinic has never
   * issued" are the same emptiness over different windows, and only a window
   * that runs up to today can support the second claim.
   */
  readonly includesToday: boolean;
}

const LAST_30_DAYS = 30;
const LAST_90_DAYS = 90;

/**
 * The most columns the trend chart will draw. Past this the columns are thinner
 * than the gaps between them, so a finer granularity shows less rather than
 * more. `resolveGranularity` coarsens rather than truncates: dropping buckets
 * would silently change what the chart claims.
 */
export const MAX_TREND_BUCKETS = 120;

const DAY_GRANULARITY_MAX_DAYS = 31;
const WEEK_GRANULARITY_MAX_DAYS = 120;

/** A Monday, used as the origin for weekday arithmetic. */
const EPOCH_MONDAY = '1970-01-05';

/**
 * Today in the *viewer's* calendar.
 *
 * Not `todayIsoUtc()`: the clinic keeps Asia/Colombo time, so between local
 * midnight and 05:30 the UTC date is still yesterday — "Today" would silently
 * mean the day before for anyone opening the page early in the morning. The
 * device clock is the closest thing the client has to the clinic's wall clock,
 * and the server owns every conversion from there.
 */
export function todayLocalIso(): string {
  const now = new Date();
  return (
    String(now.getFullYear()).padStart(4, '0') +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

/** Monday of the week containing `iso` — what the "This week" chip means. */
export function startOfWeek(iso: string): string {
  const weekday = ((diffDays(iso, EPOCH_MONDAY) % 7) + 7) % 7;
  return addDays(iso, -weekday);
}

/** The 1st of the month containing `iso` — what the "This month" chip means. */
export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function rangeLengthInDays(range: CalendarRange): number {
  if (!isValidIsoDate(range.from) || !isValidIsoDate(range.to)) return 0;
  return Math.max(0, diffDays(range.to, range.from)) + 1;
}

/**
 * The range a preset means, ending today.
 *
 * `custom` is excluded from the parameter type rather than handled with a throw:
 * the one preset that cannot answer this question is the one this function
 * cannot be called with.
 */
export function presetRange(preset: FixedPeriodPreset, today: string): CalendarRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'week':
      return { from: startOfWeek(today), to: today };
    case 'month':
      return { from: startOfMonth(today), to: today };
    case 'last30':
      return { from: addDays(today, -(LAST_30_DAYS - 1)), to: today };
    case 'last90':
      return { from: addDays(today, -(LAST_90_DAYS - 1)), to: today };
  }
}

/**
 * Make a user-supplied range usable: repair the shapes a hand-edited URL or a
 * half-filled custom form can produce, rather than sending them to the server to
 * come back as a 400 the user cannot act on.
 *
 * A reversed range is swapped (the user picked the two dates in the order they
 * thought of them, not the order the API wants), a malformed bound falls back to
 * `fallback`, and an over-long range keeps its *end* — a window is anchored on
 * the recent side, so trimming the old end is the change that surprises least.
 */
export function clampRange(range: CalendarRange, fallback: CalendarRange): CalendarRange {
  const from = isValidIsoDate(range.from) ? range.from : fallback.from;
  const to = isValidIsoDate(range.to) ? range.to : fallback.to;
  const ordered = from <= to ? { from, to } : { from: to, to: from };
  if (diffDays(ordered.to, ordered.from) + 1 <= MAX_RANGE_DAYS) return ordered;
  return { from: addDays(ordered.to, -(MAX_RANGE_DAYS - 1)), to: ordered.to };
}

/**
 * The oldest start `clampRange` will honour against a given end — the `min` of
 * the custom range's start field.
 *
 * Stating the bound in the control is what keeps the fields and the panels
 * telling the same story: an over-long range typed into a date picker is one
 * that cannot be picked, rather than one that is silently trimmed after the
 * fact. It does not replace `clampRange` — the range can still arrive from a
 * hand-edited URL, where there is no control to have stopped it.
 *
 * `undefined` for an end that is not a date yet: there is nothing to measure the
 * bound from, and an unconstrained field is better than an arbitrary one.
 */
export function earliestFromFor(to: string): string | undefined {
  return isValidIsoDate(to) ? addDays(to, -(MAX_RANGE_DAYS - 1)) : undefined;
}

/**
 * An upper bound on how many columns `bucket` would draw over `range`.
 *
 * Deliberately an over-estimate derived from the day count rather than an exact
 * figure derived from bucket boundaries: the exact answer needs the server's
 * truncation rules, and this is only ever compared against a readability
 * threshold where being one column pessimistic costs nothing.
 */
export function maxBucketCount(range: CalendarRange, bucket: BucketGranularity): number {
  const days = rangeLengthInDays(range);
  if (days === 0) return 0;
  switch (bucket) {
    case 'day':
      return days;
    case 'week':
      return Math.ceil(days / 7) + 1;
    case 'month':
      return Math.ceil(days / 28) + 1;
  }
}

/**
 * The granularity a range asks for when the user hasn't said. A month of days, a
 * quarter of weeks, anything longer in months — the same shape a reader would
 * pick by hand.
 */
export function defaultGranularity(range: CalendarRange): BucketGranularity {
  const days = rangeLengthInDays(range);
  if (days <= DAY_GRANULARITY_MAX_DAYS) return 'day';
  if (days <= WEEK_GRANULARITY_MAX_DAYS) return 'week';
  return 'month';
}

/** True when `bucket` draws a readable number of columns over `range`. */
export function granularityFits(range: CalendarRange, bucket: BucketGranularity): boolean {
  return maxBucketCount(range, bucket) <= MAX_TREND_BUCKETS;
}

/**
 * The granularity to actually request: the user's choice while it still fits the
 * range, otherwise the finest granularity that does.
 *
 * Degrading rather than discarding matters because the control offers exactly
 * the granularities that fit. Falling back to `defaultGranularity` would answer
 * a stretched-out "Day" with months while "Week" sat enabled beside it — the
 * page moving further from the user's preference than it had to, and disagreeing
 * with its own control about what was drawable.
 *
 * `chosen` is nullable rather than always-present because "follow the range" is a
 * real state and not the same as "day" — it is what lets the granularity track a
 * period change until the moment the user overrides it, and stay put afterwards.
 */
export function resolveGranularity(
  range: CalendarRange,
  chosen: BucketGranularity | null,
): BucketGranularity {
  if (chosen === null) return defaultGranularity(range);
  if (granularityFits(range, chosen)) return chosen;
  // `BUCKET_GRANULARITIES` runs finest to coarsest, so the first that fits is
  // the least coarsening the range allows. A range with nothing at all that fits
  // is not reachable — `MAX_RANGE_DAYS` of months is well inside the budget —
  // but the range still has to answer, so it answers with its own default.
  return BUCKET_GRANULARITIES.find((bucket) => granularityFits(range, bucket))
    ?? defaultGranularity(range);
}

/**
 * A bucket key as an axis tick. Formatted in UTC deliberately: the key names a
 * calendar day, and formatting it in the viewer's zone would shift a behind-UTC
 * reader's labels back by one.
 */
export function bucketLabel(key: string, bucket: BucketGranularity, lang: string): string {
  const date = new Date(`${key}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return key;
  const locale = localeFor(lang);
  switch (bucket) {
    case 'day':
      return new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }).format(date);
    case 'week':
      return new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }).format(date);
    case 'month':
      return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(date);
  }
}

/** A calendar date in full, for headings and the log's timestamps. */
export function calendarLabel(iso: string, lang: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(localeFor(lang), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * An instant as a date and time — the issue log's timestamp column.
 *
 * The zone is deliberately *not* pinned, unlike every other formatter in this
 * file: an issue happened at a moment, and the moment is what the reader wants
 * in their own clock. Only the language is fixed, so a row cannot print its
 * timestamp in the browser's locale next to an expiry date in the app's.
 */
export function timestampLabel(instant: string, lang: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  return new Intl.DateTimeFormat(localeFor(lang), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Tick text for every bucket, thinned to at most `maxTicks` labels — the rest
 * come back as empty strings, which `StackedColumnChart` renders as no tick at
 * all. Ninety daily columns cannot each carry a date, and how many they *can*
 * carry depends on how wide the screen is, so the decision belongs to the page.
 */
export function spacedBucketLabels(
  keys: readonly string[],
  bucket: BucketGranularity,
  lang: string,
  maxTicks: number,
): string[] {
  if (maxTicks <= 0) return keys.map(() => '');
  const step = keys.length > maxTicks ? Math.ceil(keys.length / maxTicks) : 1;
  return keys.map((key, i) => (i % step === 0 ? bucketLabel(key, bucket, lang) : ''));
}
